import type { TrackingSpecs } from '@/libraries/tracking/specTracker';
import type { Session } from '@/libraries/tracking/sessions';
import type { Route } from 'vue-router';

import { SnowplowSearchContext } from '@/helpers/tracking/providers';

import { createSpecTracker, defineSpec } from '@/libraries/tracking/specTracker';
import { createSessionManager } from '@/libraries/tracking/sessions';

import { useRoute, useRouter } from 'vue-router/composables';
import { useDebounceFn, createSharedComposable, tryOnScopeDispose } from '@vueuse/core';
import { onMounted } from 'vue';

const specs = {
	/** Open search suggester */
	openSearch: {
		category: 'search',
		action: 'open',
	},

	/** Close search suggester */
	closeSearch: {
		category: 'search',
		action: 'close',
	},

	/** Submit a search term (enter) or open search results (show all). */
	submitSearch: defineSpec<{
		label: 'enter' | 'show_all';
	}>()({
		category: 'search',
		action: 'more',
	}),

	/** Click on title in recent titles, results dropdown, or search page results. */
	clickTitle: defineSpec<{
		label: 'recent' | 'suggestion' | 'results';
	}>()({
		category: 'userinteraction',
		action: 'title_clicked',
		property: 'search',
	}),

	/** When suggestions get refreshed and new title impressions are sent. */
	refreshSuggestions: {
		category: 'title',
		action: 'impression',
		label: 'suggestion',
		property: 'search',
	},

	/** Impression on the empty suggester state for recently visited search titles. */
	recentlyVisitedTitles: {
		category: 'title',
		action: 'impression',
		label: 'recent',
		property: 'search',
	},

	/** Click on suggested previous or popular search term. */
	clickSuggestedTerm: defineSpec<{
		action: 'recent_searched' | 'try_searching_for';
	}>()({
		category: 'search',
	}),

	clearPreviousResults: {
		category: 'search',
		action: 'clear_all',
	},

	clickSuggestedPerson: {
		category: 'search',
		action: 'person',
	},
} satisfies TrackingSpecs;

const SearchSessionId = Symbol('SearchSession');
type SessionStart = { name: string; path: string };

function useSearchTrackingDef() {
	let previousSession: Session<SnowplowSearchContext> | null = null;

	/* SESSION */
	const { session, attachSession, killSession, stopInterval } = createSessionManager<
		SnowplowSearchContext,
		SessionStart
	>({
		id: SearchSessionId,
		influenced: true,
		options: { onSessionTimeout: prevSession => (previousSession = prevSession) },
	});

	/* TRACKING */
	// as soon as search starts we also start a session so we can
	// use the context in there instead of a computed with the context
	const trackSearch = createSpecTracker({ specs, baseContexts: () => [session()?.data ?? null] });

	/* SESSION MANAGEMENT */
	function killSearchSession() {
		previousSession = killSession();
	}

	tryOnScopeDispose(() => stopInterval());

	const route = useRoute();
	onMounted(() => {
		if (route.name === 'app.search.list' && session() == null)
			startNewSession({ name: 'app.search.list', path: route.path });
	});

	// Start search session
	function startNewSession(start: SessionStart) {
		const prevSessionUuid = previousSession?.data.sessionUuid ?? session()?.data.sessionUuid ?? null;
		previousSession = null;

		attachSession(start, new SnowplowSearchContext(crypto.randomUUID(), prevSessionUuid));
	}

	trackSearch.on('openSearch', () => startNewSession({ name: route.name!, path: route.path }));

	// Update session data
	const updateSearchSession = useDebounceFn((newQuery: string) => {
		if (session() == null) startNewSession({ name: route.name!, path: route.path });

		const currentSession = session()!;
		const searchContext = currentSession.data;

		// We only want to track empty strings when the query was deleted *during* a search session.
		// New sessions always start with an empty query right, but we don't want to track those.
		if (!searchContext.searchEntries?.length && newQuery.trim() === '') return;

		searchContext.addSearchEntry(newQuery);

		currentSession.createdAt = Date.now();
	}, 500);

	// End search session
	const router = useRouter();
	router.beforeEach((to, from, next) => {
		if (to.name == null || from.name == null) {
			killSearchSession();
			next();
			return;
		}

		const currentSession = session();

		const startIsDetailPage = currentSession?.start.name.startsWith('app.detail');
		const startIsSearchPage = currentSession?.start.name === 'app.search.list';

		// prettier-ignore
		const shouldKillSession =
			// there's a session to kill
			currentSession != null &&
			(
				// Not going to search page but also not a detail page, Popular for example should kill
				(to.name !== 'app.search.list' && !to.name.startsWith('app.detail')) ||

				// from Search Page to non detail page
				(
					currentSession.start.name === 'app.search.list' &&
					from.name === 'app.search.list' &&
					!to.name?.startsWith('app.detail')
				) ||

				// Session started on Detail page or Search Page and you go to another different title
				(
					!isGoingBetweenShowAndSeason(from, to) &&
					(startIsDetailPage || startIsSearchPage) &&
					to.name?.startsWith('app.detail') &&
					from.name !== 'app.search.list' &&
					from.path !== currentSession.start.path
				)
			);

		if (shouldKillSession) killSearchSession();

		next();
	});

	return {
		trackSearch,
		killSearchSession,
		updateSearchSession,
	};
}

/** `true` if navigating between the show and season of the same title. */
export function isGoingBetweenShowAndSeason(from: Route | null, to: Route | null) {
	if (!from || !to) return false;

	// Normalize base show path (with paths like: /us/tv-show/show-name[/season-X])
	const fromShowPath = from.path.split('/').slice(0, 4).join('/');
	const toShowPath = to.path.split('/').slice(0, 4).join('/');

	// Only allow navigations within the same show
	if (fromShowPath !== toShowPath) return false;

	const toSeason = to.name === 'app.detail.show.season';
	const fromSeason = from.name === 'app.detail.show.season';

	// Valid navigations:
	// - season -> show
	// - show -> season
	// - season -> season (same show)
	return (
		(fromSeason && to.name === 'app.detail.show') ||
		(from.name === 'app.detail.show' && toSeason) ||
		(fromSeason && toSeason)
	);
}

export const useSearchTracking = createSharedComposable(useSearchTrackingDef);
